import api from "../../../utils/api";
import { TEAM_ENDPOINTS } from "@torre/team";
import type {
  TeamMember,
  Squad,
  PayRate,
  WorkSettlement,
  PaymentCalculation,
  Contractor,
  ContractorEngagement,
  SummaryTeamMembers,
  PayMode,
  PageMeta,
  TeamMemberBonus,
} from "../types";

// ---------------------------------------------------------------------------
// Cliente API de la mini-app Equipo. Habla con el módulo
// `teamMembers` de torre-de-control-api (rutas de @torre/team).
//
// Dos envelopes de respuesta:
//   • pageResponse → { data: [...], meta }            (listas)
//   • response     → { data: { message, data } }      (single / acciones)
// `unwrap` colapsa ambos al payload interno.
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap(res: any) {
  const d = res?.data?.data;
  if (d && typeof d === "object" && !Array.isArray(d) && "data" in d) {
    return d.data;
  }
  return d;
}

export interface ListParams {
  page?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

// --- Colaboradores (AgroCloud#11) ----------------------------------------------------
export async function fetchTeamMembers(
  params: ListParams = {},
): Promise<{ items: TeamMember[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.teamMembersList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createTeamMember(payload: Partial<TeamMember>): Promise<TeamMember> {
  const res = await api.post(TEAM_ENDPOINTS.teamMember, payload);
  return unwrap(res);
}

export async function updateTeamMember(
  id: string,
  payload: Partial<TeamMember>,
): Promise<TeamMember> {
  const res = await api.put(TEAM_ENDPOINTS.teamMember, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteTeamMember(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.teamMember, { params: { id } });
}

// --- Squads (AgroCloud#12) ------------------------------------------------------
export async function fetchSquads(
  params: ListParams = {},
): Promise<{ items: Squad[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.squadsList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createSquad(payload: Partial<Squad>): Promise<Squad> {
  const res = await api.post(TEAM_ENDPOINTS.squads, payload);
  return unwrap(res);
}

export async function updateSquad(
  id: string,
  payload: Partial<Squad>,
): Promise<Squad> {
  const res = await api.put(TEAM_ENDPOINTS.squads, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteSquad(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.squads, { params: { id } });
}

export interface AssignSquadPayload {
  squadId: string;
  payRateId: string;
  mode?: PayMode;
  output?: number;
  days?: number;
  hours?: number;
  progressPercentage?: number;
  previouslyPaid?: number;
  periods?: number;
  manualBonus?: number;
  date?: string | null;
  projectId?: string | null;
  costCenterId?: string | null;
  notes?: string | null;
}

/** Asigna un squad completo a una tarea/release (AgroCloud#12). */
export async function assignSquad(
  payload: AssignSquadPayload,
): Promise<{ settlement: WorkSettlement; payment: PaymentCalculation; members: number }> {
  const res = await api.post(TEAM_ENDPOINTS.squadAssign, payload);
  return unwrap(res);
}

// --- Tarifas de pago (AgroCloud#13) ------------------------------------------------
export async function fetchPayRates(
  params: ListParams = {},
): Promise<{ items: PayRate[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.payRatesList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createPayRate(payload: Partial<PayRate>): Promise<PayRate> {
  const res = await api.post(TEAM_ENDPOINTS.payRate, payload);
  return unwrap(res);
}

export async function updatePayRate(
  id: string,
  payload: Partial<PayRate>,
): Promise<PayRate> {
  const res = await api.put(TEAM_ENDPOINTS.payRate, payload, { params: { id } });
  return unwrap(res);
}

export async function deletePayRate(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.payRate, { params: { id } });
}

// --- Liquidaciones de equipo (AgroCloud#13) -----------------------------------
export async function fetchSettlements(
  params: ListParams = {},
): Promise<{ items: WorkSettlement[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.workSettlementsList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createSettlement(
  payload: Partial<WorkSettlement>,
): Promise<WorkSettlement> {
  const res = await api.post(TEAM_ENDPOINTS.workSettlement, payload);
  return unwrap(res);
}

export async function updateSettlement(
  id: string,
  payload: Partial<WorkSettlement>,
): Promise<WorkSettlement> {
  const res = await api.put(TEAM_ENDPOINTS.workSettlement, payload, {
    params: { id },
  });
  return unwrap(res);
}

export async function deleteSettlement(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.workSettlement, { params: { id } });
}

/**
 * Vista previa del cálculo del pago (AgroCloud#13) — no persiste nada. El cálculo local
 * de `@torre/team` cubre el formulario en vivo; este verbo lo confirma
 * contra la tarifa vigente en el backend.
 */
export async function calculateSettlement(payload: {
  payRateId: string;
  mode?: PayMode;
  output?: number;
  days?: number;
  hours?: number;
  progressPercentage?: number;
  previouslyPaid?: number;
  periods?: number;
  manualBonus?: number;
}): Promise<PaymentCalculation & { payRateName?: string; outputUnit?: string }> {
  const res = await api.post(TEAM_ENDPOINTS.workSettlementCalculation, payload);
  return unwrap(res);
}

// --- Contratistas (AgroCloud#60) ----------------------------------------------------
export async function fetchContractors(
  params: ListParams = {},
): Promise<{ items: Contractor[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.contractorsList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createContractor(
  payload: Partial<Contractor>,
): Promise<Contractor> {
  const res = await api.post(TEAM_ENDPOINTS.contractors, payload);
  return unwrap(res);
}

export async function updateContractor(
  id: string,
  payload: Partial<Contractor>,
): Promise<Contractor> {
  const res = await api.put(TEAM_ENDPOINTS.contractors, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteContractor(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.contractors, { params: { id } });
}

// --- Tareas / servicios de contratista (AgroCloud#60) -------------------------------
export async function fetchContractorEngagements(
  params: ListParams = {},
): Promise<{ items: ContractorEngagement[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.contractorEngagementsList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createContractorEngagement(
  payload: Partial<ContractorEngagement>,
): Promise<ContractorEngagement> {
  const res = await api.post(TEAM_ENDPOINTS.contractorEngagement, payload);
  return unwrap(res);
}

export async function updateContractorEngagement(
  id: string,
  payload: Partial<ContractorEngagement>,
): Promise<ContractorEngagement> {
  const res = await api.put(TEAM_ENDPOINTS.contractorEngagement, payload, {
    params: { id },
  });
  return unwrap(res);
}

export async function deleteContractorEngagement(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.contractorEngagement, { params: { id } });
}

/** Liquida/paga a un contratista por el avance de su tarea (AgroCloud#60). */
export async function settleContractorEngagement(
  id: string,
  amount?: number,
): Promise<{ engagement: ContractorEngagement; paid: number; pendingBalance: number }> {
  const res = await api.post(
    TEAM_ENDPOINTS.contractorEngagementSettle,
    amount === undefined ? {} : { amount },
    { params: { id } },
  );
  return unwrap(res);
}

// --- Bonos individuales con vigencia (#1365) -------------------------------------
export async function fetchTeamBonuses(
  params: ListParams = {},
): Promise<{ items: TeamMemberBonus[]; meta: PageMeta }> {
  const res = await api.get(TEAM_ENDPOINTS.teamBonusList, {
    params: { limit: 100, page: 1, ...params },
  });
  return { items: res.data?.data ?? [], meta: res.data?.meta ?? {} };
}

export async function createTeamBonus(payload: Partial<TeamMemberBonus>): Promise<TeamMemberBonus> {
  const res = await api.post(TEAM_ENDPOINTS.teamBonus, payload);
  return unwrap(res);
}

export async function updateTeamBonus(id: string, payload: Partial<TeamMemberBonus>): Promise<TeamMemberBonus> {
  const res = await api.put(TEAM_ENDPOINTS.teamBonus, payload, { params: { id } });
  return unwrap(res);
}

export async function deleteTeamBonus(id: string): Promise<void> {
  await api.delete(TEAM_ENDPOINTS.teamBonus, { params: { id } });
}

// --- Resumen ---------------------------------------------------------------
export async function fetchSummary(): Promise<SummaryTeamMembers> {
  const res = await api.get(TEAM_ENDPOINTS.teamSummary);
  return unwrap(res);
}
